import styled from '@emotion/styled';
import { Slider } from '@mui/material';
import { Button, Label } from 'flowbite-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { MultiValue } from 'react-select';
import Select from 'react-select';
import Datepicker, { DateValueType } from 'react-tailwindcss-datepicker';
import {
	Dropdown,
	formatCareerPathType,
	MAX_LEVEL,
	MAX_PREDICTED_SALARY,
	MIN_LEVEL,
	MIN_PREDICTED_SALARY,
} from 'shared-utils';
import { uniqueSpecializations } from 'shared-utils';
import { uniqueDegrees } from 'shared-utils';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { FilterUniversityStudentsInput, ProctoringStatus, UserCareerPathType } from 'types';

export const PrettoSlider = styled(Slider)({
	color: '#7777E7',
	height: 8,
	'& .MuiSlider-rail': {
		background: '#E5E7EB',
		opacity: 1,
	},
	'& .MuiSlider-track': {
		border: 'none',
	},
	'& .MuiSlider-thumb': {
		height: 24,
		width: 24,
		backgroundColor: '#fff',
		border: '2px solid #E5E7EB',
		'&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
			boxShadow: 'inherit',
		},
		'&::before': {
			display: 'none',
		},
	},
	'& .MuiSlider-valueLabel': {
		lineHeight: '16px',
		fontSize: '12px',
		padding: 0,
		fontWeight: '500',
		fontFamily: 'Inter, sans-serif',
		width: 32,
		height: 32,
		top: '-35px',
		borderRadius: 0,
		color: '#28293D',
		backgroundColor: 'transparent',
		transformOrigin: 'bottom left',
		transform: 'none',
		'&::before': { display: 'none' },
		'&.MuiSlider-valueLabelOpen': {
			transform: 'none',
		},
	},
});

const UniversityStudentsFilters = ({
	onClose,
	onApply,
	onClear,
}: {
	onClose?: () => void;
	onApply?: () => void;
	onClear?: () => void;
}) => {
	const dateFormat = 'YYYY-MM-DD';
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');
	const toast = useToast();
	const queryClient = useQueryClient();
	const [filters, setFilters] = useState<FilterUniversityStudentsInput | null>();
	const [inputValue, setInputValue] = useState('');
	const [value, setValue] = useState<MultiValue<Dropdown>>([]);
	const [currentRounds, setCurrentRounds] = useState<MultiValue<Dropdown>>([]);
	const [currentSpecialisation, setCurrentSpecialisation] = useState<MultiValue<Dropdown>>([]);
	const { setUniversityStudentsFilters } = useUserContext();
	const [specialisationOptions, setSpecialisationOptions] = useState<Dropdown[]>([]);
	const [currentDegree, setCurrentDegree] = useState<MultiValue<Dropdown>>([]);
	const [degreeOptions, setDegreeOptions] = useState<Dropdown[]>([]);
	const [aiInterview, setAiInterview] = useState<MultiValue<Dropdown>>([]); // Modified: Added state for AI Interview
	const [assessment, setAssessment] = useState<MultiValue<Dropdown>>([]); // Modified: Added state for Assessment
	const [careerPath, setCareerPath] = useState<MultiValue<Dropdown>>([]); // Modified: Added state for Career Path

	useEffect(() => {
		setDegreeOptions(
			uniqueDegrees?.map(el => ({
				label: el,
				value: el,
			})),
		);
		setSpecialisationOptions(
			uniqueSpecializations?.map(el => ({
				label: el,
				value: el,
			})),
		);
	}, []);

	const CredibilityOptions = [
		{
			label: 'High',
			value: ProctoringStatus.High,
		},
		{
			label: 'Medium',
			value: ProctoringStatus.Medium,
		},
		{
			label: 'Low',
			value: ProctoringStatus.Low,
		},
	];

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				const filtersData = {
					...filters,
					aiInterview: aiInterview?.[0]?.value || '', // Single value instead of array
					assessment: assessment?.[0]?.value || '',
					assessmentType: currentRounds?.map(r => r.value) || '',
					careerPaths: JSON.stringify((careerPath?.map(r => r.value) as UserCareerPathType[]) || []),
					predictedSalary:
						filters?.predictedSalary?.min === MIN_PREDICTED_SALARY &&
						filters?.predictedSalary?.max === MAX_PREDICTED_SALARY
							? undefined
							: filters?.predictedSalary,
					level: filters?.level?.min === MIN_LEVEL && filters?.level?.max === MAX_LEVEL ? undefined : filters?.level,
					specialization: JSON.stringify(currentSpecialisation?.map(el => el.value)),
					degree: JSON.stringify(currentDegree?.map(el => el.value) || []),
					credibility: filters?.credibility,
					meetingFromDate: fromDate ? new Date(fromDate) : undefined,
					meetingToDate: toDate ? new Date(toDate) : undefined,
				};
				setUniversityStudentsFilters(filtersData);
			}}
		>
			{/* Filter for Career Path */}
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="currentCareerPath" // Modified: Changed for Career Path
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Career Path"
					/>
				</div>

				<Select
					isMulti
					isClearable
					options={Object.values(UserCareerPathType).map(el => ({
						label: formatCareerPathType(el),
						value: el,
					}))}
					onChange={val => {
						setCareerPath(val); // Modified: Store single value as array for consistency with other filters
						setFilters(prev => ({ ...prev }));
						console.log('Career Path Selected:', val);
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. Software Development"
					blurInputOnSelect={false}
					closeMenuOnSelect={false} // Changed to close menu after single selection
					value={careerPath || null} // Modified: Display single selected option or null
				/>
			</div>
			{/* Filter for AI Interview */}
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="currentAiInterviewType" // Modified: Changed for AI Interview
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="AI Interview"
					/>
				</div>

				<Select
					isClearable
					isMulti={false} // Changed to allow single selection only
					options={[
						{ label: 'Completed', value: 'Interview' },
						{ label: 'Not Completed', value: 'noInterview' },
					]}
					onChange={val => {
						setAiInterview(val ? [val] : []); // Modified: Store single value as array for consistency with other filters
						setFilters(prev => ({ ...prev }));
						console.log('AI Interview Selected:', val);
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. Completed"
					blurInputOnSelect={false}
					closeMenuOnSelect={true} // Changed to close menu after single selection
					value={aiInterview[0] || null} // Modified: Display single selected option or null
				/>
			</div>

			{/* Filter for Assessment Test */}
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="currentAssessmentType"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Assessment Test"
					/>
				</div>

				<Select
					isClearable
					isMulti={false} // Changed to allow single selection only
					options={[
						{ label: 'Completed', value: 'Assessment' },
						{ label: 'Not Completed', value: 'noAssessment' },
					]}
					onChange={val => {
						setAssessment(val ? [val] : []); // Modified: Store single value as array for consistency with other filters
						setFilters(prev => ({ ...prev }));
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. Completed"
					blurInputOnSelect={false}
					closeMenuOnSelect={true} // Changed to close menu after single selection
					value={assessment[0] || null} // Modified: Display single selected option or null
				/>
			</div>

			{/* Filter for Assessment Type */}

			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="currentAssessmentType"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Assessment Type"
					/>
				</div>

				<Select
					isClearable
					isMulti
					options={
						['Software Development', 'Data Analytics'].map(l => ({
							label: l.toString(),
							value: l.toString(),
						})) || []
					}
					onChange={(vals: MultiValue<Dropdown>) => {
						setCurrentRounds(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. Software Development"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={currentRounds}
				/>
			</div>

			{/* Filter for User Degree */}
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label htmlFor="userDegree" className="font-[700] font-inter text-sm text-[#28293D]" value="Degree" />
				</div>

				<Select
					isClearable
					isMulti
					options={degreeOptions}
					onChange={(vals: MultiValue<Dropdown>) => {
						setCurrentDegree(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. B.Tech"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={currentDegree}
				/>
			</div>

			{/* Filter for User Specialization */}
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="userSpecialization"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Specialization"
					/>
				</div>

				<Select
					isClearable
					isMulti
					options={specialisationOptions}
					onChange={(vals: MultiValue<Dropdown>) => {
						setCurrentSpecialisation(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. Computer Science"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={currentSpecialisation}
				/>
			</div>

			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label htmlFor="credibility" className="font-[700] font-inter text-sm text-[#28293D]" value="Credibility" />
				</div>

				<div className="flex items-center flex-wrap gap-2 w-full">
					{CredibilityOptions.map(el => (
						<div key={el.value} className="flex-1">
							<Button
								className="w-full"
								onClick={() =>
									setFilters(prev => {
										return { ...prev, credibility: el.value };
									})
								}
								color={filters?.credibility === el.value ? 'dark' : 'light'}
							>
								{el.label}
							</Button>
						</div>
					))}
				</div>
			</div>

			{/* Filter for range date */}
			<div className="mt-[28px]">
				<div className="mb-6 block">
					<Label
						htmlFor="createdAt"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Meeting Date Range"
					/>
				</div>

				<Datepicker
					useRange={false}
					value={{
						startDate: moment(fromDate).toDate(),
						endDate: moment(toDate).toDate(),
					}}
					onChange={(event: DateValueType) => {
						if (event) {
							const [startDate, endDate] = [event.startDate, event.endDate];
							setFromDate(startDate ? moment(startDate).format(dateFormat) : '');
							setToDate(endDate ? moment(endDate).format(dateFormat) : '');
							setFilters(prev => {
								return { ...prev };
							});
						}
					}}
					inputClassName="w-full rounded-md border border-[#d1d5db] font-inter focus:ring-0 font-normal bg-[#FFF] placeholder:text-[#555770]"
					placeholder="Select a date range"
				/>
			</div>

			{/* Filter for Score */}
			<div className="mt-[28px]">
				<div className="mb-6 block">
					<Label htmlFor="level" className="font-[700] font-inter text-sm text-[#28293D]" value="Overall score" />
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					aria-label="level"
					max={1000}
					min={1}
					// getAriaLabel={index => (index === 0 ? 'Minimum level' : 'Maximum level')}
					defaultValue={[MIN_LEVEL, MAX_LEVEL]}
					value={[filters?.level?.min || MIN_LEVEL, filters?.level?.max || MAX_LEVEL]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, level: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>
			</div>

			{/* Filter for Predicted Salary Type */}
			<div className="mt-[28px]">
				<div className="mb-6 block">
					<Label
						htmlFor="predictedSalary"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Prospect - predicted salary"
					/>
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					aria-label="Expected Salary"
					max={80}
					min={1}
					defaultValue={[MIN_PREDICTED_SALARY, MAX_PREDICTED_SALARY]}
					value={[
						filters?.predictedSalary?.min || MIN_PREDICTED_SALARY,
						filters?.predictedSalary?.max || MAX_PREDICTED_SALARY,
					]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, predictedSalary: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>

				<div className="flex justify-between text-sm text-[#555770] dark:text-inherit  mt-[-10px]">
					<p>1 LPA</p>
					<p>80 LPA</p>
				</div>
			</div>

			<div className="flex flex-wrap gap-[20px] mt-[17px] sticky bottom-4">
				<Button
					type="reset"
					color="light"
					className="flex-1"
					onClick={() => {
						setCareerPath([]);
						setAiInterview([]);
						setAssessment([]);
						setValue([]);
						setInputValue('');
						setCurrentRounds([]);
						setFilters(null);
						setUniversityStudentsFilters({} as FilterUniversityStudentsInput);
						setCurrentSpecialisation([]);
						setCurrentDegree([]);
						setFromDate('');
						setToDate('');
						onClear && onClear();
					}}
				>
					Clear
				</Button>
				<Button
					// disabled={filterCandidatesFn.isLoading}
					// isProcessing={filterCandidatesFn.isLoading}
					type="submit"
					color="purplePlaced"
					className="flex-1 bg-[#7777E7]"
					onClick={onApply}
				>
					Apply
				</Button>
			</div>
		</form>
	);
};

export default UniversityStudentsFilters;
